import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import http from "../../utils/axios";

const storedToken =
  typeof localStorage !== "undefined"
    ? localStorage.getItem("authToken")
    : null;
const storedUser =
  typeof localStorage !== "undefined" ? localStorage.getItem("authUser") : null;

const initialState = {
  token: storedToken || null,
  user: storedUser ? JSON.parse(storedUser) : null,
  isAuthenticated: Boolean(storedToken),
  isLoading: false,
  passwordChangeStatus: "idle",
};

// Update admin password
export const updateAdminPassword = createAsyncThunk(
  "auth/updateAdminPassword",
  async ({ current_password, new_password, new_password_confirmation }, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/auth/change-password", {
        current_password,
        new_password,
        new_password_confirmation,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update password"
      );
    }
  }
);

// Update business password
export const updateBusinessPassword = createAsyncThunk(
  "auth/updateBusinessPassword",
  async ({ current_password, new_password, new_password_confirmation }, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/auth/change-password", {
        current_password,
        new_password,
        new_password_confirmation,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update password"
      );
    }
  }
);

// Update business password (first-time) - uses different endpoint
export const updateBusinessPasswordFirstTime = createAsyncThunk(
  "auth/updateBusinessPasswordFirstTime",
  async ({ new_password, new_password_confirmation }, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/auth/business/update-password", {
        new_password,
        new_password_confirmation,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update password"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials(state, action) {
      const { token, user } = action.payload || {};
      state.token = token || null;
      state.user = user || null;
      state.isAuthenticated = Boolean(token);

      if (token) {
        localStorage.setItem("authToken", token);
      } else {
        localStorage.removeItem("authToken");
      }

      if (user) {
        localStorage.setItem("authUser", JSON.stringify(user));
      } else {
        localStorage.removeItem("authUser");
      }
    },
    setLoading(state, action) {
      state.isLoading = action.payload;
    },
    logout(state) {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      // Clear subscription data on logout
      localStorage.removeItem("business_subscription");
    },
    resetPasswordChangeStatus(state) {
      state.passwordChangeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Update admin password
      .addCase(updateAdminPassword.pending, (state) => {
        state.passwordChangeStatus = "loading";
        state.isLoading = true;
      })
      .addCase(updateAdminPassword.fulfilled, (state) => {
        state.passwordChangeStatus = "success";
        state.isLoading = false;
      })
      .addCase(updateAdminPassword.rejected, (state) => {
        state.passwordChangeStatus = "failed";
        state.isLoading = false;
      })
      // Update business password
      .addCase(updateBusinessPassword.pending, (state) => {
        state.passwordChangeStatus = "loading";
        state.isLoading = true;
      })
      .addCase(updateBusinessPassword.fulfilled, (state) => {
        state.passwordChangeStatus = "success";
        state.isLoading = false;
        // Clear authentication after business password change
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        // Clear subscription data
        localStorage.removeItem("business_subscription");
      })
      .addCase(updateBusinessPassword.rejected, (state) => {
        state.passwordChangeStatus = "failed";
        state.isLoading = false;
      })
      // Update business password (first-time)
      .addCase(updateBusinessPasswordFirstTime.pending, (state) => {
        state.passwordChangeStatus = "loading";
        state.isLoading = true;
      })
      .addCase(updateBusinessPasswordFirstTime.fulfilled, (state) => {
        state.passwordChangeStatus = "success";
        state.isLoading = false;
        // Clear authentication after first-time business password change
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        // Clear subscription data
        localStorage.removeItem("business_subscription");
      })
      .addCase(updateBusinessPasswordFirstTime.rejected, (state) => {
        state.passwordChangeStatus = "failed";
        state.isLoading = false;
      });
  },
});

export const { setCredentials, setLoading, logout, resetPasswordChangeStatus } = authSlice.actions;
export default authSlice.reducer;
